import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, Card, CardContent, Checkbox, FormControlLabel, Link, TextField, Typography, } from "@mui/material";
import { useEffect } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAddExistingPlayersToTeam, useRetrieveAddablePlayers, useRetrieveTeam, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { FormActions } from "../utils/FormActions";
import { useNumericParams } from "../utils/useNumericParams";
import { playerDisplayName } from "./PlayerDisplayNameLink";
import { countryCodeToEmoji } from "./countries";
export const AddExistingPlayersToTeam = () => {
    const { id: teamId } = useNumericParams();
    const navigate = useNavigate();
    const { data: userTeamPermissions, isLoading: userTeamPermissionsIsLoading, error: userTeamPermissionsError, } = useRetrieveUserTeamPermissions({ teamId });
    const { canModify = false } = userTeamPermissions || {};
    const { data: team, isLoading: teamIsLoading, error: teamError, } = useRetrieveTeam({ id: teamId, enabled: canModify });
    const { data: addablePlayersData = [], isLoading: addablePlayersIsLoading, error: addablePlayersError, } = useRetrieveAddablePlayers({
        teamId,
        enabled: canModify,
    });
    const { control, setValue, handleSubmit, setError, clearErrors, formState: { errors }, } = useForm({
        defaultValues: { players: [] },
    });
    const { fields } = useFieldArray({ control, name: "players" });
    const watchedPlayers = useWatch({ control, name: "players" });
    useEffect(() => {
        if (addablePlayersData.length > 0 && fields.length === 0) {
            const sorted = [...addablePlayersData].sort((a, b) => playerDisplayName(a).localeCompare(playerDisplayName(b)));
            setValue("players", sorted.map((p) => ({
                playerId: p.id,
                selected: false,
                squadNumber: null,
                displayName: playerDisplayName(p),
                nationality: p.nationality ?? null,
            })));
        }
    }, [addablePlayersData, fields.length, setValue]);
    const addPlayersMutation = useAddExistingPlayersToTeam({
        teamId,
        onMutate: () => { },
        onSuccess: () => {
            navigate(`/teams/${teamId}/`);
        },
        onError: () => { },
    });
    if (userTeamPermissionsIsLoading ||
        teamIsLoading ||
        addablePlayersIsLoading) {
        return _jsx(Loading, {});
    }
    if (userTeamPermissionsError) {
        return _jsx(ErrorView, { error: userTeamPermissionsError });
    }
    if (teamError) {
        return _jsx(ErrorView, { error: teamError });
    }
    if (addablePlayersError) {
        return _jsx(ErrorView, { error: addablePlayersError });
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    const selectedCount = (watchedPlayers || []).filter((p) => p.selected).length;
    const handleSelectAll = () => {
        const allSelected = selectedCount === fields.length;
        fields.forEach((_, i) => {
            setValue(`players.${i}.selected`, !allSelected);
        });
    };
    const onSubmit = (data) => {
        const selected = data.players.filter((p) => p.selected);
        if (selected.length === 0) {
            setError("root", { message: "Select at least one player to add." });
            return;
        }
        clearErrors("root");
        const players = selected.map((p) => ({
            playerId: p.playerId,
            ...(p.squadNumber != null ? { squadNumber: p.squadNumber } : {}),
        }));
        addPlayersMutation.mutate({ players });
    };
    return (_jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, sx: { maxWidth: 800, mx: "auto", p: 3, mb: 6 }, children: [_jsxs(Typography, { variant: "h4", sx: { textAlign: "center", mb: 1, fontWeight: 600 }, children: ["Add Players to", " ", _jsx(Link, { component: RouterLink, to: `/teams/${teamId}/`, underline: "hover", children: team.name })] }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { textAlign: "center", mb: 4 }, children: "Select the players you want to add to this team" }), fields.length === 0 ? (_jsxs(Card, { sx: { p: 4, textAlign: "center" }, children: [_jsx(Typography, { variant: "h6", sx: { mb: 2, color: "text.secondary" }, children: "No Players Available" }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 3 }, children: "You don't have any players available to add to this team. All your players are either already in this team or you haven't created any players yet." }), _jsx(Button, { component: RouterLink, to: `/teams/${teamId}/add-player/create`, variant: "contained", sx: { mr: 2 }, children: "Create New Player" }), _jsx(Button, { component: RouterLink, to: `/teams/${teamId}/add-player/`, variant: "outlined", children: "Back" })] })) : (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                            mb: 3,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }, children: [_jsxs(Typography, { variant: "h6", children: [selectedCount, " of ", fields.length, " players selected"] }), _jsx(Button, { onClick: handleSelectAll, variant: "outlined", size: "small", children: selectedCount === fields.length ? "Deselect All" : "Select All" })] }), _jsx(Box, { sx: { mb: 4 }, children: fields.map((field, index) => {
                            const isSelected = watchedPlayers?.[index]?.selected ?? false;
                            return (_jsx(Card, { sx: {
                                    mb: 2,
                                    border: isSelected ? "2px solid" : "1px solid",
                                    borderColor: isSelected ? "primary.main" : "grey.300",
                                    bgcolor: isSelected ? "primary.50" : "background.paper",
                                    cursor: "pointer",
                                }, onClick: () => setValue(`players.${index}.selected`, !isSelected), children: _jsxs(CardContent, { sx: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        py: 2,
                                    }, children: [_jsx(FormControlLabel, { control: _jsx(Controller, { name: `players.${index}.selected`, control: control, render: ({ field: checkboxField }) => (_jsx(Checkbox, { checked: checkboxField.value, onChange: (e) => checkboxField.onChange(e.target.checked), onClick: (e) => e.stopPropagation() })) }), label: "", sx: { m: 0 }, onClick: (e) => e.stopPropagation() }), _jsx(Box, { sx: { flex: 1 }, children: _jsxs(Typography, { variant: "h6", children: [field.nationality &&
                                                        `${countryCodeToEmoji(field.nationality)} `, field.displayName] }) }), isSelected && (_jsx(Controller, { name: `players.${index}.squadNumber`, control: control, render: ({ field: sqField }) => (_jsx(TextField, { value: sqField.value ?? "", onChange: (e) => {
                                                    const val = e.target.value;
                                                    sqField.onChange(val === "" ? null : parseInt(val, 10));
                                                }, label: "#", type: "number", size: "small", onClick: (e) => e.stopPropagation(), inputProps: { min: 1, max: 99 }, sx: { width: 80 }, placeholder: "1-99" })) }))] }) }, field.id));
                        }) }), errors.root && selectedCount === 0 && (_jsx(Typography, { color: "error", variant: "body2", sx: { mt: 2, textAlign: "center" }, children: errors.root.message })), _jsx(FormActions, { submitLabel: "Add Players", onCancel: () => navigate(`/teams/${teamId}/add-player/`), cancelLabel: "Back", isSubmitting: addPlayersMutation.isLoading })] }))] }));
};
